/**
 * Renders the clinical impression form fields grouped into the intake note's
 * sections (Chief Complaint, HPI, Mental Health History, …). Per-section scratch
 * notes (FR-D5) are captured earlier on the call-guide step; here they are
 * surfaced read-only via a hover icon next to each section header (shown only
 * when that section has scratch). The icon also copies the scratch to the
 * clipboard on click, so the clinician can paste it into a field directly.
 * Conditionally shows Medication History for intake encounters and the
 * pediatric sections only for patients under 18 (D10).
 *
 * Sections may also carry retired generic fields (`legacyFieldKeys`), shown only
 * where an encounter has content in them. They render read-only on a finalized
 * encounter, but editable on an in-progress draft (`legacyEditable`) so the
 * clinician can revise content authored under the pre-split generic keys.
 * @agentic connections
 */

import { useState } from "react";
import { ActionIcon, Box, Group, Stack, Title, Tooltip } from "@mantine/core";
import { useClipboard } from "@mantine/hooks";
import { IconCheck, IconNotes } from "@tabler/icons-react";
import { FormSection } from "@benchmark/ui/components/atomic";
import { FloatingLabelTextarea } from "@benchmark/ui/components/atomic";
import {
  CLINICAL_IMPRESSION_FORM_FIELDS,
  isIntakeEncounterType,
} from "@benchmark/core/constants";

import { NOTE_SECTIONS } from "./note-sections";
import { NoteSectionAudienceBadge } from "./NoteSectionAudienceBadge";

const LABEL_BY_KEY: Record<string, string> = Object.fromEntries(
  CLINICAL_IMPRESSION_FORM_FIELDS.map((f) => [f.key, f.label]),
);

/**
 * Read-only affordance for a section's call-guide scratch: hovering reveals the
 * text, clicking copies it to the clipboard so the clinician can paste it into
 * a field. Briefly confirms the copy in the tooltip and icon.
 * @agentic connections
 */
const SectionScratchHint = ({
  scratch,
  sectionId,
  sectionName,
}: {
  scratch: string;
  sectionId: string;
  sectionName: string;
}) => {
  const clipboard = useClipboard({ timeout: 1500 });

  return (
    <Tooltip
      label={clipboard.copied ? "Copied to clipboard" : scratch}
      multiline
      w={280}
      withArrow
      events={{ hover: true, focus: true, touch: true }}
    >
      <ActionIcon
        variant="subtle"
        color={clipboard.copied ? "teal" : "gray"}
        size="sm"
        aria-label={`Copy call notes for ${sectionName}`}
        data-testid={`section-scratch-hint-${sectionId}`}
        onClick={() => clipboard.copy(scratch)}
      >
        {clipboard.copied ? <IconCheck size={16} /> : <IconNotes size={16} />}
      </ActionIcon>
    </Tooltip>
  );
};

/**
 * Props for the clinical notes fields component.
 * @agentic connections
 */
export type ClinicalNotesFieldsProps = {
  encounterType: string | undefined;
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  disabled?: boolean;
  /**
   * When true, the patient is under 18 and the pediatric-only sections render.
   * Defaults to false so adult encounters never surface pediatric fields (D10).
   */
  isPediatric?: boolean;
  /**
   * Per-section scratch notes (keyed by section id) captured on the call-guide
   * step. Surfaced here read-only via a hover icon — never editable on the
   * Notes step.
   */
  sectionScratch?: Record<string, string>;
  /**
   * When true (in-progress draft), retired-generic fields that carry content
   * render editable instead of read-only, so the clinician can revise content
   * authored under the pre-split generic keys. Defaults to false — finalized
   * encounters keep the read-only "(historical)" treatment.
   */
  legacyEditable?: boolean;
};

/**
 * Clinical impression note fields, grouped by section. Each section header shows
 * a hover icon revealing the call-guide scratch for that section, when present.
 * @agentic connections
 */
export const ClinicalNotesFields = ({
  encounterType,
  values,
  onChange,
  disabled = false,
  isPediatric = false,
  sectionScratch = {},
  legacyEditable = false,
}: ClinicalNotesFieldsProps) => {
  const isIntake = isIntakeEncounterType(encounterType);

  // Retired-generic fields are editable on a draft. Their visibility must NOT
  // track the live value once editable — otherwise clearing (or select-all +
  // retype) empties the value for a beat and unmounts the textarea mid-edit, so
  // the clinician can't finish the rewrite. Snapshot (once, via lazy state) the
  // legacy keys that arrive with content on first render and keep them mounted
  // for the session.
  const [stickyLegacyKeys] = useState<Set<string>>(
    () =>
      new Set(
        NOTE_SECTIONS.flatMap((s) => s.legacyFieldKeys ?? []).filter((key) =>
          values[key]?.trim(),
        ),
      ),
  );

  return (
    <FormSection showDivider={false} title="Clinical Interaction Notes">
      <Stack gap="lg">
        {NOTE_SECTIONS.map((section) => {
          if (section.pediatric && !isPediatric) return null;
          // Inverse gate (R2): hide the adult Social History section for minors
          // — the pediatric sections cover their social/family history. But
          // never hide CONTENT: a pre-fix draft or older composition may have
          // filed social/family history under the adult keys (the Issue 3
          // defect), and that documentation must stay visible and editable on
          // resume (R4). Only hide the section when it is genuinely empty.
          if (section.audience === "adult" && isPediatric) {
            const adultHasContent = [
              ...section.fieldKeys,
              ...(section.legacyFieldKeys ?? []),
            ].some((key) => values[key]?.trim());
            if (!adultHasContent) return null;
          }

          const fieldKeys = section.fieldKeys.filter(
            (key) => key !== "medication-history" || isIntake,
          );
          // Retired generics surface only where the encounter has content in
          // them. On a finalized encounter they stay read-only and track the
          // live value (clearing would delete the ClinicalImpression). On an
          // in-progress draft they are editable and stay mounted for the
          // session once shown (sticky), so a clear/rewrite doesn't unmount the
          // textarea mid-edit.
          const legacyFieldKeys = (section.legacyFieldKeys ?? []).filter(
            (key) =>
              legacyEditable ? stickyLegacyKeys.has(key) : values[key]?.trim(),
          );
          if (fieldKeys.length === 0 && legacyFieldKeys.length === 0)
            return null;

          const scratch = sectionScratch[section.id]?.trim();
          // The adult and pediatric Social History sections share a label, so
          // the audience qualifies both the heading and the icon's a11y name.
          const sectionName = section.audience
            ? `${section.label} (${section.audience})`
            : section.label;

          return (
            <Box key={section.id}>
              <Stack gap="md">
                <Group gap={4} align="center" wrap="nowrap">
                  <Title
                    order={3}
                    size="md"
                    style={{
                      color: "var(--mantine-color-gray-7)",
                      textTransform: "uppercase",
                    }}
                  >
                    {section.label}
                  </Title>
                  {section.audience && (
                    <NoteSectionAudienceBadge audience={section.audience} />
                  )}
                  {scratch ? (
                    <SectionScratchHint
                      scratch={scratch}
                      sectionId={section.id}
                      sectionName={sectionName}
                    />
                  ) : null}
                </Group>
                {fieldKeys.map((key) => (
                  <FloatingLabelTextarea
                    key={key}
                    label={LABEL_BY_KEY[key] ?? key}
                    value={values[key] || ""}
                    onChange={(value) => onChange(key, value)}
                    disabled={disabled}
                  />
                ))}
                {legacyFieldKeys.map((key) =>
                  legacyEditable ? (
                    <FloatingLabelTextarea
                      key={key}
                      label={LABEL_BY_KEY[key] ?? key}
                      value={values[key] || ""}
                      onChange={(value) => onChange(key, value)}
                      disabled={disabled}
                    />
                  ) : (
                    <FloatingLabelTextarea
                      key={key}
                      label={`${LABEL_BY_KEY[key] ?? key} (historical)`}
                      value={values[key] || ""}
                      onChange={() => undefined}
                      disabled
                    />
                  ),
                )}
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </FormSection>
  );
};
